import { clsx } from "clsx";
import React, {
  InputHTMLAttributes,
  MouseEventHandler,
  forwardRef,
  useCallback,
} from "react";
import { FieldError } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { extractFormErrorMessage } from "@/utils/form";

type CommonInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "id"> & {
  description?: React.ReactNode;
  descriptionClassName?: string;
  label?: React.ReactNode;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  error?: FieldError;
  disabled?: boolean;
};

type FloatingInputProps = CommonInputProps & {
  labelPosition?: "floating";
  id?: string;
};

type StackedInputProps = CommonInputProps & {
  labelPosition: "stacked";
  id: string;
};

type InputProps = FloatingInputProps | StackedInputProps;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      description,
      descriptionClassName,
      leadingIcon,
      trailingIcon,
      error: errorMessage,
      placeholder: placeholderProp,
      disabled,
      labelPosition = "floating",
      id: idProp,
      ...rest
    },
    ref,
  ) => {
    const placeholder =
      placeholderProp ?? (typeof label === "string" ? label : undefined);
    const inputId = labelPosition === "stacked" ? `input-${idProp}` : undefined;
    const afterText = extractFormErrorMessage(errorMessage) || description;
    const describedById = afterText ? `${inputId}-description` : undefined;

    // For stacked layouts, need to pass focus down to the input element on click, because label is separated
    const handleInputParentClick = useCallback<
      MouseEventHandler<HTMLDivElement>
    >((e) => {
      e.currentTarget.querySelector("input")?.focus();
    }, []);

    const sharedContainerClassNames = twMerge(
      clsx(
        "relative inline-flex cursor-text items-center gap-2 rounded-lg border border-borderDefault bg-white px-4 py-2 transition focus-within:border-interactive-selected focus-within:ring-2 focus-within:ring-inset focus-within:ring-interactive-selected dark:bg-gray-100",
        errorMessage &&
          "border-borderError focus-within:border-borderError focus-within:ring-borderError",
        className,
      ),
      disabled && "bg-surface-disabled text-textSubtle opacity-40",
      !disabled && "hover:border-interactive-selected",
    );

    if (labelPosition === "stacked") {
      return (
        <div className="flex flex-col gap-2">
          {label ? (
            <label
              htmlFor={inputId}
              className={clsx(
                "text-sm",
                errorMessage ? "text-textError" : "text-textDefault",
              )}
            >
              {label}
            </label>
          ) : null}

          <div
            className={sharedContainerClassNames}
            onClick={handleInputParentClick}
          >
            {leadingIcon}

            <input
              type="text"
              className="placeholder:text-textSubtle text-textDefault peer max-w-full flex-1 outline-none"
              placeholder={placeholder}
              disabled={disabled}
              ref={ref}
              aria-invalid={!!errorMessage || undefined}
              aria-describedby={describedById}
              {...rest}
              id={inputId}
            />

            {trailingIcon}
          </div>

          {afterText ? (
            <div
              id={describedById}
              className={twMerge(
                clsx(
                  "inline-block px-4 text-xs",
                  errorMessage ? "text-textError" : "text-textSubtle",
                ),
                descriptionClassName,
              )}
            >
              {afterText}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        <label className={sharedContainerClassNames}>
          {leadingIcon}

          <input
            type="text"
            className="placeholder:text-textSubtle text-textDefault peer max-w-full flex-1 outline-none focus:placeholder-transparent"
            placeholder={placeholder}
            disabled={disabled}
            ref={ref}
            {...rest}
          />
          <span
            className={clsx(
              "peer-focus:text-interactive-selected absolute top-0 left-4 -translate-y-1/2 bg-white px-1 text-xs opacity-100 transition peer-placeholder-shown:opacity-0 peer-focus:opacity-100 dark:bg-gray-100",
              errorMessage ? "text-textError" : "text-textDefault",
            )}
          >
            {label}
          </span>

          {trailingIcon}
        </label>
        {afterText && (
          <div
            className={twMerge(
              clsx(
                "inline-block px-4 text-xs",
                errorMessage ? "text-textError" : "text-textSubtle",
              ),
              descriptionClassName,
            )}
          >
            {afterText}
          </div>
        )}
      </div>
    );
  },
);
